import { useState } from 'react'
import { claimScratchPrize, getScratchToken } from '@/lib/api/scratch-campaigns-public.service'
import { ApiClientError } from '@/lib/api/client'
import { isRaspadinhaPrizePasswordValid } from '@/lib/raspadinha-public/raspadinha-prize-password'
import {
  EMPTY_RASPADINHA_PUBLIC_ADDRESS,
  RaspadinhaPublicAddressFields,
} from './RaspadinhaPublicAddressFields'
import { RaspadinhaPublicPasswordField } from './RaspadinhaPublicPasswordField'
import styles from './RaspadinhaPublicContemplatedPrizeStep.module.css'

type RaspadinhaPublicContemplatedPrizeStepProps = {
  slug: string
  imageUrl: string
  prizeTitle: string
}

export function RaspadinhaPublicContemplatedPrizeStep({
  slug,
  imageUrl,
  prizeTitle,
}: RaspadinhaPublicContemplatedPrizeStepProps) {
  const alt = prizeTitle.trim() || 'Prêmio contemplado'
  const [password, setPassword] = useState('')
  const [address, setAddress] = useState(EMPTY_RASPADINHA_PUBLIC_ADDRESS)
  const [showPasswordValidation, setShowPasswordValidation] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const handleContinue = async () => {
    setShowPasswordValidation(true)
    setErrorMessage(null)

    if (!isRaspadinhaPrizePasswordValid(password)) {
      return
    }

    const token = getScratchToken(slug)
    if (!token) {
      setErrorMessage('Sessão expirada. Refaça o cadastro para continuar.')
      return
    }

    setIsSubmitting(true)
    try {
      await claimScratchPrize(slug, {
        password,
        postal_code: address.postalCode,
        street: address.street,
        street_number: address.streetNumber,
        complement: address.complement,
        neighborhood: address.neighborhood,
        city: address.city,
        state: address.state,
      })
      setSuccessMessage('Cadastro concluído com sucesso. Em breve entraremos em contato.')
    } catch (error) {
      const message =
        error instanceof ApiClientError
          ? error.message
          : 'Não foi possível concluir o cadastro. Tente novamente.'
      setErrorMessage(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section className={styles.step} aria-label={alt}>
      <div className={styles.imageWrap}>
        <img className={styles.prizeImage} src={imageUrl} alt={alt} draggable={false} />
      </div>

      <div className={styles.formSection}>
        <h2 className={styles.formTitle}>Complete seu cadastro para continuar</h2>

        {successMessage ? (
          <p className={styles.successMessage} role="status">
            {successMessage}
          </p>
        ) : (
          <form
            className={styles.form}
            onSubmit={(event) => {
              event.preventDefault()
              void handleContinue()
            }}
          >
            <RaspadinhaPublicPasswordField
              value={password}
              onChange={setPassword}
              showValidation={showPasswordValidation}
            />

            <RaspadinhaPublicAddressFields value={address} onChange={setAddress} />

            {errorMessage ? (
              <p className={styles.errorMessage} role="alert">
                {errorMessage}
              </p>
            ) : null}

            <button type="submit" className={styles.continueButton} disabled={isSubmitting}>
              {isSubmitting ? 'Enviando...' : 'Continuar'}
            </button>
          </form>
        )}
      </div>
    </section>
  )
}
